/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import { BrickLoader, UniversalWrap } from "~/components";
import {
  getColorsPalette,
  getTableColumns,
} from "~/components/Charts/CostAnalysis";
import { tooltipStyles } from "~/components/Charts/CostAnalysis/components";
import { getEndDate } from "~/components/Charts/CostAnalysis/helpers";
import {
  ChartType,
  Granularity,
  Period,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency, useDisclosure } from "~/hooks";
import { isEmpty } from "~/tools";
import { ApplicationCostTableData } from "../ApplicationCostTableData";
import { AnomaliesChart } from "../charts";
import { AnomaliesDetails } from "../charts/components";
import { useAnomaliesChart } from "../hooks";

type Props = {
  appId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function ApplicationAnomalyCostView({
  appId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();
  const { isOpen, close, open } = useDisclosure();
  const [selection, setSelection] = useState({
    date: "",
    anomalyCost: "",
  });
  const [changedThreshold, setChangedThreshold] = useState(0);

  const {
    anomaliesChartData,
    applicationAnomaliesTableData,
    anomaliesTotal,
    isLoading,
    error,
    refetch,
  } = useAnomaliesChart({
    appId,
    period,
    startDate,
    endDate: getEndDate(endDate),
    granularity,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  useEffect(() => {
    void refetch();
  }, [changedThreshold, refetch]);

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: applicationAnomaliesTableData,
        hasFooter,
        period,
        chartType: ChartType.ANOMALY_COST,
        colorsPalette: getColorsPalette(ChartType.ANOMALY_COST),
      }),
    [
      startDate,
      endDate,
      currency,
      hasFooter,
      applicationAnomaliesTableData,
      period,
    ]
  );

  const handleDetailsClick = (date: string, anomalyCost: string) => {
    setSelection({
      date,
      anomalyCost,
    });
    open();
    hideTooltip();
  };

  return (
    <UniversalWrap
      loaderProps={{ loading: isLoading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <div role="presentation" onMouseLeave={hideTooltip}>
        <ParentSize>
          {(parent) => (
            <>
              <AnomaliesChart
                width={parent.width}
                height={280}
                loading={isLoading}
                anomalies={anomaliesChartData}
                granularity={granularity}
                startDate={startDate}
                endDate={endDate}
                period={period}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                handleDetailsClick={handleDetailsClick}
                containerRef={containerRef}
              />
              <When condition={tooltipOpen}>
                <TooltipInPortal
                  // set key to random so it correctly updates with parent bounds
                  key={Date.now()}
                  top={tooltipTop}
                  left={tooltipLeft}
                  style={tooltipStyles}
                >
                  {tooltipData}
                </TooltipInPortal>
              </When>
            </>
          )}
        </ParentSize>
      </div>
      <ApplicationCostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={applicationAnomaliesTableData}
        chartType={ChartType.ANOMALY_COST}
        total={anomaliesTotal}
        tableError={error}
        columns={columns}
        isLoading={isLoading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
        setChangedThreshold={setChangedThreshold}
        isEmptyData={isEmpty(anomaliesChartData) && period === Period.MONTH}
      />
      <AnomaliesDetails
        css={{ pointerEvents: "none" }}
        isSidebarOpen={isOpen}
        date={selection.date}
        anomalyCost={selection.anomalyCost}
        onClose={close}
      />
    </UniversalWrap>
  );
}
